import { Mail } from 'lucide-react';
import {
  CampaignAccessDenied,
  CampaignPageShell,
  CampaignTabs,
} from '@/client/components/admin/Campaigns/CampaignShell';
import EmailComposer from '@/client/components/admin/Email/EmailComposer';
import { SignatureSettings } from '@/client/components/admin/Email/Signatures';
import CampaignSendHistory from '@/client/components/admin/Campaigns/CampaignSendHistory';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';

const EMAIL_TABS = ['send', 'history', 'signatures'] as const;
type Tab = (typeof EMAIL_TABS)[number];

const EmailPage = () => {
  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/email?tab=send' },
    { label: 'Email' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const sendPerm = useHasPermission('messaging.send');
  const overridePerm = useHasPermission('messaging.override');
  const templatePerm = useHasPermission('messaging.template');
  const canEditSignatures = templatePerm.isSuperAdmin || templatePerm.hasPermission;

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canSend = sendPerm.isSuperAdmin || sendPerm.hasPermission;
  const canOverride = overridePerm.isSuperAdmin || overridePerm.hasPermission;
  const permLoading = viewPerm.isLoading || sendPerm.isLoading;

  const [tab, setTab] = useTabSearchParam<Tab>(EMAIL_TABS, 'send');
  const tabs = [
    ...(canSend ? [{ id: 'send' as const, label: 'Compose' }] : []),
    ...(canView ? [{ id: 'history' as const, label: 'Send history' }] : []),
    ...(canEditSignatures ? [{ id: 'signatures' as const, label: 'Signatures' }] : []),
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id ?? 'history';

  if (permLoading) {
    return (
      <CampaignPageShell
        loading
        icon={<Mail className="h-4 w-4 text-white" />}
        title="Email"
        description=""
      >
        {null}
      </CampaignPageShell>
    );
  }

  if (!canView && !canSend) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <CampaignAccessDenied code="messaging.view" />
        </div>
      </div>
    );
  }

  return (
    <CampaignPageShell
      icon={<Mail className="h-4 w-4 text-white" />}
      title="Email"
      description="Write to one person or a few. Mail to many goes through Campaigns."
    >
      <CampaignTabs tabs={tabs} active={activeTab} onChange={setTab} />

      {activeTab === 'send' && canSend && (
        <EmailComposer canOverride={canOverride} />
      )}
      {activeTab === 'history' && canView && <CampaignSendHistory channel="email" />}
      {activeTab === 'signatures' && canEditSignatures && <SignatureSettings />}
    </CampaignPageShell>
  );
};

export default EmailPage;
